import styles from './Experts.module.less'
import HeaderComp from 'src/components/common/layout/header/Header'
import { ExpertsTable } from './expertsTable/Table'
import { Flex, Layout, TableProps, message } from 'antd'
import { FilterSection } from './filterSection/FilterSection'
import { TagSection } from './tagSection/TagSection'
import React, { useState, useMemo, useEffect, useCallback } from 'react'
import useTag from 'src/hooks/useTag'
import SidebarPanel from './sidebarPanel/SidebarPanel'
import { Loader } from 'src/components/common/loader/Loader'
import { convertToCSV, downloadCsvFile } from 'src/utils/common.utils'
import { UIButton } from 'src/components/common/uiButton/UIButton'
import { ExpertTableDataType, ExportDetailsRespType } from 'src/types/api.types'
import ExpertsProfileModal from './expertsProfileModal/ExpertsProfileModal'
import { CSVFileName, ExportTableHeader, EmptyTableText } from 'src/constants/experts.constants'
import { useExpertsData } from 'src/hooks/useExpertsData'
import { TextConstants } from 'src/constants/ui.constants'
import { PaginationType } from 'src/types/expert.types'
import SegmentAnalytics from 'src/SegmentAnalytics'

const { SearchForExperts, FetchingData, ExportSuccessful, ExpertsSelected } =
    TextConstants.ExpertsPage

// eslint-disable-next-line
type RowExtractor<T> = (item: T) => any[]
const { Sider, Content } = Layout

export const ExpertsPage: React.FC = () => {
    const [selectedRows, setSelectedRows] = useState<ExpertTableDataType[]>([])
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
    const [isFilterVisible, setIsFilterVisible] = useState(false)
    const [pagination, setPagination] = useState<PaginationType>({ page: 1, pageSize: 10 })
    const { tagsList, removedTagName, updateTags, removeTags, removeAllTags } = useTag({
        setPagination,
    })
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [expertId, setExpertId] = useState('')
    const {
        filtersOptionsQuery,
        skillsListQuery,
        expertsProfileQuery,
        expertsListQuery,
        exportDetailsQuery,
    } = useExpertsData(expertId, tagsList, pagination, selectedRows)

    useEffect(() => {
        SegmentAnalytics.sendTrackEvent('EE_Page_Loaded_Successfully', {})
    }, [])

    useEffect(() => {
        setPagination({ page: 1, pageSize: 10 })
    }, [expertsListQuery.error])

    useEffect(() => {
        setSelectedRows([])
    }, [tagsList.length, pagination.page])

    const toggleSidebar = (val: boolean) => {
        setIsSidebarCollapsed(val)
    }

    const toggleFilterVisibility = () => {
        setIsFilterVisible(prev => !prev)
    }

    const handleDownloadCSV = async () => {
        const { data: exportDetails } = await exportDetailsQuery.refetch()

        const rowExtractor: RowExtractor<ExportDetailsRespType> = expert => [
            expert.authalId,
            expert.name,
            expert.email,
            expert.skills,
            expert.level,
            expert.location,
            expert.languages.join(', '),
            expert.yearsOfExperience,
            expert.expertiseAreas.join(', '),
            expert.occupation,
            expert.educationDetails,
            expert.skillValidationDetails,
            expert.lifeCycleStage,
            expert.talentStudioProfile,
        ]

        downloadCsvFile(convertToCSV(exportDetails, ExportTableHeader, rowExtractor), CSVFileName)
        message.success(ExportSuccessful)

        SegmentAnalytics.sendTrackEvent('EE_ExportToCSVCTA_Clicked', {
            expertsAuthalId: new Set(exportDetails?.map(val => val.authalId)),
        })
    }

    const rowSelection: TableProps<ExpertTableDataType>['rowSelection'] = {
        selectedRowKeys: selectedRows.map(row => row.key),
        onChange: (_, selectedRows: ExpertTableDataType[]) => {
            setSelectedRows(selectedRows)
        },
    }

    const handleModalVisibility = (id: string) => {
        setExpertId(id)
        setIsModalOpen(true)
    }

    const emptyTableText = useMemo(() => {
        return expertsListQuery.data?.expertsList
            ? EmptyTableText.NoExpertsFound
            : EmptyTableText.NoFiltersApplied
    }, [expertsListQuery.data])

    const handlePagination = useCallback((page: number, pageSize: number) => {
        setPagination({ page, pageSize })
        SegmentAnalytics.sendTrackEvent('Table_PaginationValue_Change', {
            page,
            pageSize,
        })
    }, [])

    if (filtersOptionsQuery.isLoading || skillsListQuery.isLoading) {
        return <Loader title={FetchingData} />
    }

    return (
        <Layout className={styles.layoutStyle}>
            <HeaderComp />
            <Flex className={styles.headerpanel} justify="space-between" align="center">
                <h2 className={styles.headerText}>{SearchForExperts}</h2>
                <Flex align="center" gap={'20px'}>
                    {selectedRows.length > 0 && (
                        <p className={styles.selectedExperts}>
                            {selectedRows.length} {ExpertsSelected}
                        </p>
                    )}
                    <UIButton
                        title={`Export ${selectedRows.length > 0 ? 'selected' : 'all'} to CSV`}
                        isDisabled={selectedRows.length === 0}
                        type="default"
                        width={selectedRows.length > 0 ? '180px' : '140px'}
                        isLoading={exportDetailsQuery.isLoading}
                        onClick={handleDownloadCSV}
                    />
                </Flex>
            </Flex>
            <Layout>
                <Sider
                    width="292px"
                    className={`${styles.siderStyle} ${
                        isSidebarCollapsed ? styles.hideSection : ''
                    }`}
                >
                    <SidebarPanel
                        toggleSidebarMenu={toggleSidebar}
                        updateTags={updateTags}
                        removedTagName={removedTagName}
                        skillsList={skillsListQuery.data}
                        removeTags={removeTags}
                    />
                </Sider>
                <Content className={styles.contentStyle}>
                    <Flex className={styles.filterSection}>
                        <Flex className={` ${!isFilterVisible ? styles.hideSection : ''}`}>
                            <FilterSection
                                updateTags={updateTags}
                                removedTagName={removedTagName}
                                filterOption={filtersOptionsQuery.data}
                            />
                        </Flex>
                        <TagSection
                            toggleSidebar={toggleSidebar}
                            isSidebarCollapsed={isSidebarCollapsed}
                            toggleFilterVisibility={toggleFilterVisibility}
                            isFilterVisible={isFilterVisible}
                            tags={tagsList}
                            removeTags={removeTags}
                            removeAllTags={removeAllTags}
                        />
                    </Flex>
                    <ExpertsTable
                        dataSource={expertsListQuery.data?.expertsList || []}
                        isLoading={expertsListQuery.isLoading}
                        isFilterVisible={isFilterVisible}
                        pagination={{ page: pagination.page, pageSize: pagination.pageSize }}
                        total={expertsListQuery.data?.limit ?? 0}
                        onUpdatePagination={handlePagination}
                        rowSelection={rowSelection}
                        setIsModalOpen={handleModalVisibility}
                        emptyTableText={emptyTableText}
                        isSidebarCollapsed={isSidebarCollapsed}
                    />
                    <ExpertsProfileModal
                        open={!expertsProfileQuery.error ? isModalOpen : false}
                        onCancel={() => setIsModalOpen(false)}
                        expertsProfile={expertsProfileQuery.data}
                        isLoading={expertsProfileQuery.isLoading}
                    />
                </Content>
            </Layout>
        </Layout>
    )
}
